import React, { useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { useGenerateGHADiscountToken } from '~/hooks/apis/connection-google/useGHADiscountRules';

import { GHADiscountRule } from '~/services/ConnectionGoogleService';
import { Dayjs, getDayJs } from '~/services/TimeService';
import { customerPortalHost } from '~/services/common';

import { formatRuleDiscount } from './utils';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

interface Props {
  open: boolean;
  rule: GHADiscountRule | null;
  onClose: () => void;
}

function buildValidationError(
  rule: GHADiscountRule | null,
  checkIn: Dayjs | null,
  checkOut: Dayjs | null,
): string | null {
  if (!rule || !checkIn || !checkOut) {
    return null;
  }
  if (!checkIn.isValid() || !checkOut.isValid()) {
    return 'Pick valid check-in and check-out dates.';
  }
  const today = getDayJs().startOf('day');
  const checkInDay = checkIn.startOf('day');
  const checkOutDay = checkOut.startOf('day');

  if (!checkOutDay.isAfter(checkInDay)) {
    return 'Check-out must be after check-in.';
  }

  const abw = checkInDay.diff(today, 'day');
  if (abw < rule.minAdvanceBookingWindow) {
    return `Advance booking window is ${abw} day(s); rule requires at least ${rule.minAdvanceBookingWindow}.`;
  }

  const los = checkOutDay.diff(checkInDay, 'day');
  if (los < rule.minLengthOfStay) {
    return `Length of stay is ${los} night(s); rule requires at least ${rule.minLengthOfStay}.`;
  }

  return null;
}

function buildMetasearchLink(rule: GHADiscountRule, checkIn: Dayjs, checkOut: Dayjs, token?: string) {
  const customerPortalTenant = { value: rule.accountType === 'scoopon' ? 'SCOOPON' : 'LUX' };
  const url = new URL(`${customerPortalHost(customerPortalTenant)}/metasearch/${rule.propertyId}`);
  url.searchParams.set('checkIn', checkIn.format(ISO_DATE_FORMAT));
  url.searchParams.set('checkOut', checkOut.format(ISO_DATE_FORMAT));
  url.searchParams.set('utm_bp', '3');
  url.searchParams.set('source', 'metasearch');
  url.searchParams.set('ruleId', 'le_standard_live_aud');

  if (token) {
    url.searchParams.set('gpid', token);
  }

  return url.toString();
}

function GHADiscountTokenDialog({ open, rule, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const generateToken = useGenerateGHADiscountToken();

  // Keep `reset` reachable from the effect without subscribing the effect to
  // the mutation result's identity (which changes on every render).
  const resetMutationRef = useRef(generateToken.reset);
  resetMutationRef.current = generateToken.reset;

  const [checkIn, setCheckIn] = useState<Dayjs | null>(null);
  const [checkOut, setCheckOut] = useState<Dayjs | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Re-seed defaults only when the dialog opens for a different rule. Keying
  // on rule.id (stable string) instead of the rule object avoids a render
  // loop when query refetches produce new object identities.
  const ruleId = rule?.id ?? null;
  const minAbw = rule?.minAdvanceBookingWindow ?? 0;
  const minLos = rule?.minLengthOfStay ?? 1;

  useEffect(() => {
    if (!open || !ruleId) {
      return;
    }
    const nextCheckIn = getDayJs().add(Math.max(minAbw, 0), 'day');
    const nextCheckOut = nextCheckIn.add(Math.max(minLos, 1), 'day');
    setCheckIn(nextCheckIn);
    setCheckOut(nextCheckOut);
    setToken(null);
    resetMutationRef.current();
  }, [open, ruleId, minAbw, minLos]);

  const validationError = buildValidationError(rule, checkIn, checkOut);
  const canSubmit = !!rule && !!checkIn && !!checkOut && !validationError && !generateToken.isPending;
  const discountLink =
    rule && checkIn && checkOut && token ? buildMetasearchLink(rule, checkIn, checkOut, token) : null;
  const standardLink = rule && checkIn && checkOut && token ? buildMetasearchLink(rule, checkIn, checkOut) : null;

  const handleSubmit = async () => {
    if (!rule || !checkIn || !checkOut) {
      return;
    }
    try {
      const response = await generateToken.mutateAsync({
        ruleId: rule.id,
        checkIn: checkIn.format(ISO_DATE_FORMAT),
        checkOut: checkOut.format(ISO_DATE_FORMAT),
      });
      setToken(response.token);
    } catch {
      // Snackbar shown by the hook.
    }
  };

  const handleCopy = async (value: string, itemName: 'token' | 'link') => {
    if (!value) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar(`${itemName === 'token' ? 'Token' : 'Link'} copied to clipboard`, { variant: 'success' });
    } catch {
      enqueueSnackbar(`Could not copy ${itemName}`, { variant: 'error' });
    }
  };

  const handleClose = () => {
    if (generateToken.isPending) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate test discount token</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {rule && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Rule: {formatRuleDiscount(rule)} · Min ABW {rule.minAdvanceBookingWindow} · Min LOS{' '}
                {rule.minLengthOfStay}
              </Typography>
            </Box>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DatePicker
              label="Check-in"
              value={checkIn}
              onChange={(value) => {
                setCheckIn(value);
                setToken(null);
              }}
              format="DD/MM/YYYY"
              minDate={getDayJs()}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="Check-out"
              value={checkOut}
              onChange={(value) => {
                setCheckOut(value);
                setToken(null);
              }}
              format="DD/MM/YYYY"
              minDate={checkIn ?? getDayJs()}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Stack>
          {validationError && <Alert severity="warning">{validationError}</Alert>}
          {token && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Token</Typography>
              <TextField
                value={token}
                multiline
                minRows={2}
                maxRows={6}
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <Tooltip title="Copy token">
                        <IconButton onClick={() => handleCopy(token, 'token')} size="small">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  },
                }}
              />
              {discountLink && standardLink && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Customer portal links</Typography>
                  <TextField
                    label="With discount"
                    value={discountLink}
                    fullWidth
                    slotProps={{
                      input: {
                        readOnly: true,
                        startAdornment: (
                          <Link href={discountLink} target="_blank" rel="noopener noreferrer" sx={{ mr: 1 }}>
                            Open
                          </Link>
                        ),
                        endAdornment: (
                          <Tooltip title="Copy link">
                            <IconButton onClick={() => handleCopy(discountLink, 'link')} size="small">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ),
                      },
                    }}
                  />
                  <TextField
                    label="Without discount"
                    value={standardLink}
                    fullWidth
                    slotProps={{
                      input: {
                        readOnly: true,
                        startAdornment: (
                          <Link href={standardLink} target="_blank" rel="noopener noreferrer" sx={{ mr: 1 }}>
                            Open
                          </Link>
                        ),
                        endAdornment: (
                          <Tooltip title="Copy link">
                            <IconButton onClick={() => handleCopy(standardLink, 'link')} size="small">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ),
                      },
                    }}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={generateToken.isPending}>
          Close
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          Generate
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GHADiscountTokenDialog;
